import { useState } from "react";
import { Link } from "react-router-dom";
import { Languages, Sparkles, Users, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const LANG_LABEL = { fr: "Français", en: "English", zh: "中文" };

export async function translate(kind, id, target) {
  const { data } = await api.post("/ai/translate", { kind, id, target });
  return data;
}

export const TranslationBox = ({ t, onClose }) => (
  <div className="mt-1.5 border-l-2 border-mr-accent pl-2 text-sm" data-testid="translation-box">
    <p className="text-[11px] text-mr-muted flex items-center gap-1"><Languages size={11} /> Traduction automatique · {LANG_LABEL[t.target]}
      {onClose && <button className="ml-1 underline" data-testid="translation-close" onClick={onClose}>masquer</button>}</p>
    <p className="whitespace-pre-line" data-testid="translation-text" lang={t.target === "zh" ? "zh-Hans" : t.target}>{t.translation}</p>
  </div>
);

export function Translate({ kind, id, testId = "translate" }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState("");
  const go = async (target) => {
    setBusy(target);
    try {
      setT(await translate(kind, id, target));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1 text-xs text-mr-muted"><Languages size={13} /> Traduire :
        {Object.entries(LANG_LABEL).map(([k, l]) => (
          <button key={k} data-testid={`${testId}-${k}`} disabled={!!busy} className={`px-2 py-0.5 border rounded-full ${t?.target === k ? "border-mr-accent text-mr-text" : "border-mr-border"}`} onClick={() => go(k)}>{busy === k ? "…" : l}</button>
        ))}
      </div>
      {t && <TranslationBox t={t} onClose={() => setT(null)} />}
    </div>
  );
}

const RecoCard = ({ r }) => {
  const Icon = r.type === "membre" ? Users : Briefcase;
  return (
    <Link to={r.type === "membre" ? `/app/reseau/${r.id}` : `/app/operations/${r.id}`} className="mr-card block p-3 hover:border-mr-accent transition-colors" data-testid={`reco-${r.type}`}>
      <p className="font-medium flex items-center gap-2"><Icon size={15} className="text-mr-accent" /> {r.title}{r.contact && <span className="text-[11px] text-mr-muted">· déjà en contact</span>}</p>
      <p className="text-sm text-mr-text2 mt-1" data-testid="reco-reason">{r.reason}</p>
      <p className="text-[11px] text-mr-muted mt-1">Source : {r.source}</p>
    </Link>
  );
};

export function Recommendations() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setRes((await api.post("/ai/recommendations")).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mr-card space-y-3" data-testid="recommendations-card">
      <p className="mr-eyebrow">Recommandations</p>
      <p className="text-sm text-mr-text2">Des membres complémentaires à rencontrer et des opérations proches de votre profil, chacun expliqué.</p>
      <button data-testid="recommendations-button" className="mr-btn-primary w-full" disabled={busy} onClick={run}><Sparkles size={16} /> {busy ? "Analyse…" : res ? "Actualiser" : "Voir mes recommandations"}</button>
      {res && (
        <div className="space-y-3" data-testid="recommendations-results">
          <p className="text-xs text-mr-muted">{res.notice}</p>
          <p className="text-sm font-medium">Membres à rencontrer</p>
          {res.members.length ? res.members.map((r) => <RecoCard key={r.id} r={r} />) : <p className="text-sm text-mr-muted" data-testid="reco-members-empty">Aucun membre complémentaire trouvé pour l'instant.</p>}
          <p className="text-sm font-medium">Opérations qui correspondent</p>
          {res.operations.length ? res.operations.map((r) => <RecoCard key={r.id} r={r} />) : <p className="text-sm text-mr-muted" data-testid="reco-ops-empty">Aucune opération ouverte ne correspond pour l'instant.</p>}
        </div>
      )}
    </div>
  );
}

export function AdmissionSummary({ appId }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setRes((await api.post(`/ai/admission-summary/${appId}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-mr-border rounded-[3px] p-3 space-y-2" data-testid="admission-ai-summary">
      <div className="flex items-center justify-between gap-2"><p className="font-medium">Résumé du dossier (assistant)</p>
        <button data-testid="admission-ai-summary-button" className="mr-btn-secondary px-3 min-h-[36px]" disabled={busy} onClick={run}><Sparkles size={14} /> {busy ? "Synthèse…" : res ? "Régénérer" : "Résumer"}</button></div>
      {res && <>
        <p data-testid="admission-ai-summary-text">{res.summary}</p>
        {res.facts.length > 0 && <ul className="text-xs space-y-0.5">{res.facts.map((f, i) => <li key={i}><span className="text-mr-muted">{f.label} :</span> {f.value} <span className="text-mr-muted">[{f.source}]</span></li>)}</ul>}
        {res.to_check.length > 0 && <div data-testid="admission-ai-to-check"><p className="text-xs text-mr-muted">À vérifier</p><ul className="list-disc pl-5 text-xs">{res.to_check.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        <p className="text-[11px] text-mr-warning" data-testid="admission-ai-notice">{res.notice}</p>
      </>}
    </div>
  );
}
